import { useState } from 'react';
import { getMsalInstance } from '../auth';
import { patchCriterionV2, assignCriterionPositionsV2 } from '../api/v2/criteria';
import type { CriterionAPIV2, PositionAPIV2 } from '../types';

interface Props {
  tenderId: string;
  criteria: CriterionAPIV2[];
  /** Detected positions (roles), for role labels and the Needs-a-role chips. */
  positions: PositionAPIV2[];
  /** When set, Requirements/Responsibilities are filtered to the criteria bound
   *  to this position ordinal (the role selected in the roles rail). */
  selectedOrdinal: number | null;
  /** Clears the selected-role filter. */
  onClearSelectedOrdinal: () => void;
  /** Called with the updated criterion after a successful activate/deactivate
   *  or role assignment. */
  onCriterionUpdated: (updated: CriterionAPIV2) => void;
}

/** Current user's email (the resolvable identifier), or null when auth is off. */
function currentUserEmail(): string | null {
  return getMsalInstance().getActiveAccount()?.username ?? null;
}

/** Initials from a display name, e.g. "Jeremiah Crouch" → "JC". */
function toInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Format an ISO instant as "22 May". */
function formatChangedAt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

const ordinalsOf = (c: CriterionAPIV2): number[] =>
  Array.isArray(c.position_ordinals) ? c.position_ordinals : [];

/** Does this criterion have any source context to show? */
function hasSourceText(c: CriterionAPIV2): boolean {
  return Boolean((c.source_section && c.source_section.trim()) || (c.source_quote && c.source_quote.trim()));
}

/**
 * The criterion's source context, shown inline (scrollable) — the full section
 * it was read from, with the tight cited quote called out above it. This is the
 * "next higher level" of context: the requirement in place, not an isolated
 * sentence. Falls back to the quote alone when no section is available.
 */
function SourcePanel({ section, quote }: { section?: string; quote?: string }) {
  const sectionText = section?.trim() ? section : undefined;
  const quoteText = quote?.trim() ? quote : undefined;
  const body = sectionText ?? quoteText;
  if (!body) return null;
  const showQuoteSeparately = quoteText && sectionText && quoteText !== sectionText;
  return (
    <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 px-2.5 py-2 max-h-52 overflow-y-auto">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
        {sectionText ? 'Source section' : 'Source text'}
      </p>
      {showQuoteSeparately && (
        <p className="text-[11px] text-gray-500 italic mb-1.5 leading-snug">Cited: &ldquo;{quoteText}&rdquo;</p>
      )}
      <p className="text-[11px] text-gray-600 leading-snug whitespace-pre-line">{body}</p>
    </div>
  );
}

type Tab = 'REQUIREMENT' | 'RESPONSIBILITY' | 'NEEDS_A_ROLE';

/**
 * Third-column "Tender criteria" rail. Requirements / Responsibilities tabs list
 * each criterion (a checkbox, checked when active) with the role(s) it is
 * attached to. A third "Needs a role" tab surfaces held-out criteria — the ones
 * the tool could not confidently place — for the recruiter to assign; its badge
 * counts only what still needs a decision. The tab appears only once attachment
 * has produced links (before that, nothing is meaningfully "held out").
 */
export default function CriteriaRail({
  tenderId,
  criteria,
  positions,
  selectedOrdinal,
  onClearSelectedOrdinal,
  onCriterionUpdated,
}: Props) {
  const [tab, setTab] = useState<Tab>('REQUIREMENT');

  // A role is selected → show only the criteria bound to it (a UNIVERSAL
  // criterion, bound to every role, shows under each). Held-out criteria belong
  // to no role, so a role filter naturally excludes them — they stay in the
  // Needs-a-role tab, which always reads the full set.
  const inSelectedRole = (c: CriterionAPIV2): boolean =>
    selectedOrdinal === null || ordinalsOf(c).includes(selectedOrdinal);
  // Grouped by `nature`, not `kind`. The backend stopped writing kind when the
  // have/do decision moved to the enrichment stage (V106/V107), so filtering on
  // kind matched nothing and both tabs rendered empty on every tender extracted
  // since. A criterion with no nature is not the candidate's — the firm's, the
  // submission's, or unrouted — and belongs in neither tab.
  const requirements = criteria.filter((c) => c.nature === 'REQUIREMENT' && inSelectedRole(c));
  const responsibilities = criteria.filter((c) => c.nature === 'RESPONSIBILITY' && inSelectedRole(c));
  const selectedTitle =
    selectedOrdinal === null
      ? null
      : positions.find((p) => p.ordinal === selectedOrdinal)?.title ?? `Role #${selectedOrdinal}`;

  // The Needs-a-role surface only makes sense once attachment has run; before
  // that every criterion has empty ordinals and would look "held out". Computed
  // from the FULL set — the selected-role filter must not shrink it (held-out
  // criteria are role-agnostic).
  const hasAttachmentData = criteria.some((c) => ordinalsOf(c).length > 0);
  // Held out = the candidate's, meant for an individual, and bound to no role.
  // TEAM criteria are excluded because they bind no seat by design and so never
  // acquire ordinals — counting them here would present every team obligation as
  // a triage item the recruiter can never clear.
  const heldOut = hasAttachmentData
    ? criteria.filter(
        (c) =>
          (c.nature === 'REQUIREMENT' || c.nature === 'RESPONSIBILITY') &&
          c.target !== 'TEAM' &&
          ordinalsOf(c).length === 0,
      )
    : [];

  const titleByOrdinal = new Map(positions.map((p) => [p.ordinal, p.title]));
  const roleLabelFor = (c: CriterionAPIV2): string | null => {
    if (!hasAttachmentData) return null;
    const ords = ordinalsOf(c);
    if (ords.length === 0) return 'Needs a role';
    if (ords.length === positions.length) return 'All roles';
    return ords
      .map((o) => titleByOrdinal.get(o) ?? `#${o}`)
      .join(', ');
  };

  const shown = tab === 'REQUIREMENT' ? requirements : responsibilities;

  return (
    <aside className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">
          {selectedTitle ? 'Role criteria' : 'Tender criteria'}
        </h2>
      </div>

      {/* Tabs */}
      <div className="px-3 pt-3 flex gap-1 text-xs">
        <button
          type="button"
          onClick={() => setTab('REQUIREMENT')}
          className={`flex-1 py-1.5 rounded-md font-medium ${
            tab === 'REQUIREMENT' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          Requirements <span className="text-gray-400">{requirements.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab('RESPONSIBILITY')}
          className={`flex-1 py-1.5 rounded-md font-medium ${
            tab === 'RESPONSIBILITY' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          Responsibilities <span className="text-gray-400">{responsibilities.length}</span>
        </button>
        {hasAttachmentData && (
          <button
            type="button"
            onClick={() => setTab('NEEDS_A_ROLE')}
            className={`flex-1 py-1.5 rounded-md font-medium inline-flex items-center justify-center gap-1.5 ${
              tab === 'NEEDS_A_ROLE' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Needs a role
            {heldOut.length > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                {heldOut.length}
              </span>
            ) : (
              <span className="text-emerald-600" aria-label="All criteria assigned">✓</span>
            )}
          </button>
        )}
      </div>

      {selectedTitle && tab !== 'NEEDS_A_ROLE' && (
        <p className="px-4 pt-3 -mb-1 text-[11px] text-gray-500 flex items-center gap-2">
          <span>
            Showing <span className="font-medium text-gray-700">{selectedTitle}</span>
          </span>
          <button
            type="button"
            onClick={onClearSelectedOrdinal}
            className="text-gray-400 hover:text-gray-600 underline"
          >
            show all
          </button>
        </p>
      )}

      {tab === 'NEEDS_A_ROLE' ? (
        <NeedsARolePanel
          tenderId={tenderId}
          heldOut={heldOut}
          positions={positions}
          onCriterionUpdated={onCriterionUpdated}
        />
      ) : shown.length === 0 ? (
        <p className="p-5 text-[11px] text-gray-400 leading-snug">
          No {tab === 'REQUIREMENT' ? 'requirements' : 'responsibilities'}.
        </p>
      ) : (() => {
        // Split by source so recruiter-added rows render in their own
        // labelled group at the bottom of the list, matching the
        // wireframe's "Added by recruiter (N)" section.
        const extracted = shown.filter((c) => c.source !== 'RECRUITER');
        const added = shown.filter((c) => c.source === 'RECRUITER');
        return (
          <ul className="p-3 space-y-0.5 max-h-[640px] overflow-y-auto">
            {extracted.map((criterion) => (
              <CriterionRow
                key={criterion.id}
                tenderId={tenderId}
                criterion={criterion}
                roleLabel={roleLabelFor(criterion)}
                onUpdated={onCriterionUpdated}
              />
            ))}
            {added.length > 0 && (
              <li className="px-2 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Added by recruiter ({added.length})
              </li>
            )}
            {added.map((criterion) => (
              <CriterionRow
                key={criterion.id}
                tenderId={tenderId}
                criterion={criterion}
                roleLabel={roleLabelFor(criterion)}
                onUpdated={onCriterionUpdated}
              />
            ))}
          </ul>
        );
      })()}
    </aside>
  );
}

interface RowProps {
  tenderId: string;
  criterion: CriterionAPIV2;
  roleLabel: string | null;
  onUpdated: (updated: CriterionAPIV2) => void;
}

function CriterionRow({ tenderId, criterion, roleLabel, onUpdated }: RowProps) {
  const active = criterion.is_active;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [reasonMode, setReasonMode] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastChange = criterion.last_change;
  const who = lastChange?.user_name ? toInitials(lastChange.user_name) : lastChange?.user_id ?? '—';
  const needsRole = roleLabel === 'Needs a role';
  const hasSource = hasSourceText(criterion);

  async function setActive(next: boolean, reasonText: string | null) {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await patchCriterionV2(
        tenderId, criterion.id, next ? 'ACTIVE' : 'INACTIVE', currentUserEmail(), reasonText);
      onUpdated(updated);
      setReasonMode(false);
      setReason('');
    } catch {
      setError("Couldn't save — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // "Is not a role criteria" — clear the criterion's role binding so it drops
  // into the Needs-a-role grouping for re-triage (empty ordinals = held out).
  async function sendToNeedsARole() {
    setSubmitting(true);
    setError(null);
    try {
      await assignCriterionPositionsV2(tenderId, criterion.id, []);
      onUpdated({ ...criterion, position_ordinals: [] });
    } catch {
      setError("Couldn't move — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="px-2 py-2 rounded-lg hover:bg-gray-50">
      <div className="flex items-start gap-2.5">
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-snug ${active ? 'font-medium text-gray-900' : 'line-through text-gray-400'}`}>
            {criterion.title}
          </p>
          {roleLabel && (
            <p className="text-[11px] mt-1 leading-snug text-gray-500">
              {needsRole ? 'Needs a role' : roleLabel}
            </p>
          )}
          {lastChange && (
            <p
              className="text-[11px] text-gray-500 mt-1 leading-snug"
              title={lastChange.reason ?? undefined}
            >
              {active ? 'Activated' : 'Deactivated'} by {who} · {formatChangedAt(lastChange.changed_at)}
            </p>
          )}
          {criterion.added_by && (() => {
            const addedBy = criterion.added_by;
            const addedWho = addedBy.user_name ? toInitials(addedBy.user_name) : addedBy.user_id ?? '—';
            return (
              <p className="text-[11px] text-gray-500 mt-1 leading-snug italic">
                Added by {addedWho} · {formatChangedAt(addedBy.changed_at)}
                {addedBy.reason && <> · &ldquo;{addedBy.reason}&rdquo;</>}
              </p>
            );
          })()}
          {showSource && (
            <SourcePanel section={criterion.source_section} quote={criterion.source_quote} />
          )}
        </div>
        <button
          type="button"
          disabled={submitting}
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Criterion actions"
          className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-200 hover:text-gray-600 disabled:opacity-50"
        >
          <span aria-hidden="true" className="text-base leading-none">⋮</span>
        </button>
      </div>

      {menuOpen && (
        <div role="menu" className="mt-2 ml-1 w-52 rounded-md border border-gray-200 bg-white shadow-sm py-1 text-xs">
          {hasSource && (
            <MenuItem onClick={() => { setShowSource((s) => !s); setMenuOpen(false); }}>
              {showSource ? 'Hide source text' : 'View source text'}
            </MenuItem>
          )}
          {!needsRole && (
            <MenuItem onClick={() => { setMenuOpen(false); sendToNeedsARole(); }}>
              Is not a role criteria
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setMenuOpen(false);
              if (active) setReasonMode(true);
              else setActive(true, null);
            }}
          >
            {active ? 'Deactivate' : 'Activate'}
          </MenuItem>
        </div>
      )}

      {reasonMode && (
        <div className="mt-2 ml-1 space-y-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            disabled={submitting}
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            aria-label="Reason"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActive(false, reason.trim() || null)}
              disabled={submitting}
              className="inline-flex items-center rounded-md bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5"
            >
              {submitting ? 'Saving…' : 'Deactivate'}
            </button>
            <button
              type="button"
              onClick={() => { setReasonMode(false); setReason(''); }}
              disabled={submitting}
              className="inline-flex items-center rounded-md border border-gray-300 text-gray-600 text-xs font-medium px-3 py-1.5 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-1 ml-1 text-[11px] text-red-600">{error}</p>}
    </li>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 text-gray-700 hover:bg-gray-50"
    >
      {children}
    </button>
  );
}

interface NeedsPanelProps {
  tenderId: string;
  heldOut: CriterionAPIV2[];
  positions: PositionAPIV2[];
  onCriterionUpdated: (updated: CriterionAPIV2) => void;
}

const KIND_STYLE: Record<'REQUIREMENT' | 'RESPONSIBILITY', string> = {
  REQUIREMENT: 'bg-blue-50 text-blue-700',
  RESPONSIBILITY: 'bg-purple-50 text-purple-700',
};

/**
 * The "Needs a role" triage panel: held-out criteria the recruiter assigns to
 * one or more roles (or marks "Not role-specific"). Selections stage locally; a
 * single Save persists them all. Assigning clears the criterion from the tab.
 */
function NeedsARolePanel({ tenderId, heldOut, positions, onCriterionUpdated }: NeedsPanelProps) {
  // criterionId → the ordinals staged for it (a fresh selection since every card
  // here starts held out with no bindings).
  const [staged, setStaged] = useState<Record<string, number[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const allOrdinals = positions.map((p) => p.ordinal);

  const selectionFor = (id: string): number[] => staged[id] ?? [];
  const toggleOrdinal = (id: string, ordinal: number) => {
    setStaged((prev) => {
      const current = prev[id] ?? [];
      const next = current.includes(ordinal)
        ? current.filter((o) => o !== ordinal)
        : [...current, ordinal].sort((a, b) => a - b);
      return { ...prev, [id]: next };
    });
  };
  const toggleNotRoleSpecific = (id: string) => {
    setStaged((prev) => {
      const current = prev[id] ?? [];
      const isAll = current.length === allOrdinals.length && allOrdinals.length > 0;
      return { ...prev, [id]: isAll ? [] : [...allOrdinals] };
    });
  };

  const pending = heldOut.filter((c) => selectionFor(c.id).length > 0);

  async function saveAll() {
    setSaving(true);
    setError(false);
    try {
      for (const c of pending) {
        const ordinals = selectionFor(c.id);
        await assignCriterionPositionsV2(tenderId, c.id, ordinals);
        // The criterion is now bound — reflect it locally so it leaves the tab
        // and appears under its role(s). The backend returns 204; we know the
        // resulting ordinals are exactly what we sent.
        onCriterionUpdated({ ...c, position_ordinals: ordinals });
      }
      setStaged({});
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  if (heldOut.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-2xl text-emerald-500" aria-hidden="true">✓</p>
        <p className="mt-1 text-[12px] text-gray-500">Every criterion has a role.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="px-5 pt-3 pb-2">
        <p className="text-[11.5px] text-gray-500 leading-snug">
          {heldOut.length} {heldOut.length === 1 ? 'criterion' : 'criteria'} still need a decision.
          Assign a role, or mark <em>Not role-specific</em>.
        </p>
      </div>

      <ul className="px-2 pb-2 space-y-2 max-h-[560px] overflow-y-auto">
        {heldOut.map((c) => (
          <NeedsARoleCard
            key={c.id}
            criterion={c}
            positions={positions}
            selection={selectionFor(c.id)}
            onToggleOrdinal={(ordinal) => toggleOrdinal(c.id, ordinal)}
            onToggleNrs={() => toggleNotRoleSpecific(c.id)}
          />
        ))}
      </ul>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
        <span className="text-[11.5px] text-gray-500">
          {error
            ? <span className="text-red-600">Couldn&apos;t save — try again.</span>
            : `${pending.length} of ${heldOut.length} selected`}
        </span>
        <button
          type="button"
          onClick={saveAll}
          disabled={saving || pending.length === 0}
          className="h-8 px-3.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

interface NeedsCardProps {
  criterion: CriterionAPIV2;
  positions: PositionAPIV2[];
  selection: number[];
  onToggleOrdinal: (ordinal: number) => void;
  onToggleNrs: () => void;
}

/** One held-out criterion: its role chips + a "View source" toggle that expands
 *  the full source section inline (the fuller context, not just the quote). */
function NeedsARoleCard({ criterion, positions, selection, onToggleOrdinal, onToggleNrs }: NeedsCardProps) {
  const [showSource, setShowSource] = useState(false);
  // Held-out cards only ever reach here for criteria that have a nature, so the
  // fallback is unreachable — kept so a future caller cannot render an empty badge.
  const kind = criterion.nature === 'RESPONSIBILITY' ? 'RESPONSIBILITY' : 'REQUIREMENT';
  const allOrdinals = positions.map((p) => p.ordinal);
  const isAll = selection.length === allOrdinals.length && allOrdinals.length > 0;
  return (
    <li className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND_STYLE[kind]}`}>
          {kind}
        </span>
        <span className="text-[11px] font-medium text-amber-700">Needs a role</span>
        {hasSourceText(criterion) && (
          <button
            type="button"
            onClick={() => setShowSource((s) => !s)}
            className="ml-auto text-[11px] text-gray-400 hover:text-gray-600 underline"
          >
            {showSource ? 'Hide source' : 'View source'}
          </button>
        )}
      </div>
      <p className="text-[13px] leading-snug text-gray-800 mt-2">{criterion.title}</p>
      {showSource && <SourcePanel section={criterion.source_section} quote={criterion.source_quote} />}
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        {positions.map((p) => {
          const on = selection.includes(p.ordinal);
          return (
            <button
              key={p.ordinal}
              type="button"
              onClick={() => onToggleOrdinal(p.ordinal)}
              aria-pressed={on}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium border transition ${
                on
                  ? 'bg-brand-600 border-brand-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-brand-400'
              }`}
            >
              {p.title}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onToggleNrs}
          aria-pressed={isAll}
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium border border-dashed transition ${
            isAll
              ? 'bg-gray-800 border-gray-800 text-white'
              : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'
          }`}
        >
          Not role-specific
        </button>
      </div>
    </li>
  );
}
